/**
 * StoryInviteCard — "Share your Lomi story" research-volunteer opt-in.
 *
 * Pull, not push: the student taps in; we never cold-DM. Renders BOTH English
 * and Amharic stacked (so every student is addressed regardless of app
 * language). Flow: choose tier (call | video) → enter the callback number
 * Team Lomi should use (asked, never prefilled — a Telegram/auth number is
 * often not the one they want a call on) → thank-you.
 *
 * Reused in two spots:
 *   - Profile  — permanent, non-dismissible (the always-open door)
 *   - Dashboard — prominent hero, dismissible (collapses once opted-in/dismissed)
 *
 * Bilingual copy lives in i18n/strings.ts under `volunteer.*`; we read BOTH
 * locale blocks directly here rather than via useT(), because this surface
 * shows both languages at once.
 */

import React, { useEffect, useState } from 'react';
import feedbackAPI from '../services/feedbackAPI';
import { track } from '../services/api';
import { STRINGS } from '../i18n/strings';
import { useTheme } from '../contexts/ThemeContext';

type Tier = 'call' | 'video';
type Step = 'loading' | 'hidden' | 'choose' | 'phone' | 'done';

interface Props {
  source?: string; // 'web_profile' | 'dashboard'
  dismissible?: boolean;
  onDismiss?: () => void;
  // Dashboard hero: if the student already volunteered (before this mount),
  // render nothing rather than a permanent thank-you card on the home screen.
  // A fresh opt-in during this session still shows the thank-you.
  hideIfAlreadyDone?: boolean;
  // Prominent surfaces (dashboard hero): only show to engaged, non-brand-new
  // students (server gate: ≥50 answered + ≥3 days). The permanent /profile
  // entry leaves this off so it's always findable.
  requireEligible?: boolean;
  // Optional name to prefill the (optional) name field — the name on file.
  defaultName?: string;
}

const en = (k: string) => STRINGS.en[k] || '';
const am = (k: string) => STRINGS.am[k] || STRINGS.en[k] || '';

// Paused 2026-06-14 (founder): "Share your story" promos off for now, will
// resurrect later. Flip to true to bring the card back on every surface.
const STORY_PROMO_ENABLED = false;

const StoryInviteCard: React.FC<Props> = ({
  source = 'web_profile',
  dismissible = false,
  onDismiss,
  hideIfAlreadyDone = false,
  requireEligible = false,
  defaultName = '',
}) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [step, setStep] = useState<Step>('loading');
  const [tier, setTier] = useState<Tier | null>(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardStyle: React.CSSProperties = {
    background: dark
      ? 'linear-gradient(135deg, rgba(134,194,50,0.10) 0%, #171b21 100%)'
      : 'linear-gradient(135deg, #f3f9ec 0%, #ffffff 100%)',
    border: dark ? '1.5px solid #262c35' : '1.5px solid #cfe6b0',
    borderRadius: 16,
    padding: '1.1rem 1.15rem',
    color: dark ? '#e6e8eb' : undefined,
  };

  const amText: React.CSSProperties = {
    color: dark ? '#aab1ba' : '#5b6b4a',
    fontSize: '0.9rem',
  };

  useEffect(() => {
    // "Share your story" promo is paused for now (resurrect later by flipping
    // STORY_PROMO_ENABLED back to true). Self-hides everywhere this card renders.
    if (!STORY_PROMO_ENABLED) {
      setStep('hidden');
      return;
    }
    feedbackAPI
      .getVolunteer()
      .then((v) => {
        if (v.volunteer_tier) {
          setStep(hideIfAlreadyDone ? 'hidden' : 'done');
          return;
        }
        // Prominent surface: hide for ineligible (new/low-engagement) students.
        if (requireEligible && !v.story_eligible) {
          setStep('hidden');
          return;
        }
        track('story.shown', { surface: source });
        setStep('choose');
      })
      // On error, hide the prominent surface (don't show unverified); the
      // always-on profile entry (requireEligible=false) still shows.
      .catch(() => setStep(requireEligible ? 'hidden' : 'choose'));
  }, [hideIfAlreadyDone, requireEligible]);

  if (step === 'loading' || step === 'hidden') return null;

  const both = (k: string) => `${en(k)} / ${am(k)}`;

  const pickTier = (t: Tier) => {
    track('story.tier_clicked', { surface: source, tier: t });
    setTier(t);
    setError(null);
    setStep('phone');
  };

  const submit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 9) {
      setError(both('volunteer.phone.invalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await feedbackAPI.volunteer(tier || 'call', phone.trim(), name.trim(), source);
      track('story.submitted', {
        surface: source,
        tier: tier || 'call',
        has_name: !!name.trim(),
      });
      setStep('done');
    } catch {
      setError(both('volunteer.error'));
    } finally {
      setBusy(false);
    }
  };

  // ── Thank-you ──────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 0.3rem', fontSize: '1.05rem' }}>
          {en('volunteer.signedup.title')}
        </h3>
        <p style={{ margin: 0, color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
          {en('volunteer.signedup.body')}
        </p>
        <p style={{ margin: '0.5rem 0 0', ...amText }}>
          {am('volunteer.signedup.body')}
        </p>
      </div>
    );
  }

  // ── Enter the callback number ──────────────────────────────
  if (step === 'phone') {
    return (
      <div style={cardStyle}>
        <p style={{ margin: '0 0 0.15rem', fontWeight: 600, fontSize: '0.98rem' }}>
          {en('volunteer.phone.prompt')}
        </p>
        <p style={{ margin: '0 0 0.7rem', ...amText }}>
          {am('volunteer.phone.prompt')}
        </p>
        <input
          type="tel"
          inputMode="tel"
          autoFocus
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={`${en('volunteer.phone.placeholder')} · ${am('volunteer.phone.placeholder')}`}
          disabled={busy}
          style={{
            width: '100%',
            padding: '0.65rem 0.75rem',
            borderRadius: 10,
            border: dark ? '1px solid #2d333b' : '1px solid #cfe6b0',
            background: dark ? '#12161c' : undefined,
            color: dark ? '#e6e8eb' : undefined,
            fontSize: '1rem',
            boxSizing: 'border-box',
          }}
        />
        {/* Optional name — prefilled from the name on file, never required. */}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`${en('volunteer.name.label')} · ${am('volunteer.name.label')}`}
          disabled={busy}
          maxLength={120}
          style={{
            width: '100%',
            padding: '0.65rem 0.75rem',
            marginTop: '0.5rem',
            borderRadius: 10,
            border: dark ? '1px solid #262c35' : '1px solid #e2ecd3',
            background: dark ? '#12161c' : undefined,
            color: dark ? '#e6e8eb' : undefined,
            fontSize: '1rem',
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <p style={{ color: dark ? '#ff6b6b' : '#c0392b', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            onClick={submit}
            disabled={busy}
            style={{
              flex: 1,
              padding: '0.65rem',
              borderRadius: 10,
              border: 'none',
              background: dark ? '#86c232' : '#4a7c10',
              color: dark ? '#12161c' : '#fff',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? '…' : `${en('volunteer.phone.submit')} · ${am('volunteer.phone.submit')}`}
          </button>
          <button
            onClick={() => {
              setStep('choose');
              setError(null);
            }}
            disabled={busy}
            style={{
              padding: '0.65rem 0.9rem',
              borderRadius: 10,
              border: dark ? '1px solid #2d333b' : '1px solid #cfe6b0',
              background: 'transparent',
              color: dark ? '#aab1ba' : '#5b6b4a',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            ←
          </button>
        </div>
      </div>
    );
  }

  // ── Choose tier (stacked bilingual invite) ─────────────────
  const tierBtn = (t: Tier, emoji: string, key: string) => (
    <button
      onClick={() => pickTier(t)}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '0.7rem 0.85rem',
        borderRadius: 12,
        border: 'none',
        background: dark ? '#86c232' : '#4a7c10',
        color: dark ? '#12161c' : '#fff',
        fontSize: '0.97rem',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {emoji} {en(key)} · {am(key)}
    </button>
  );

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 0.1rem', fontSize: '1.05rem' }}>
        🙋 {en('volunteer.card.title')}
      </h3>
      <p style={{ margin: '0 0 0.55rem', ...amText }}>{am('volunteer.card.title')}</p>
      <p style={{ margin: '0 0 0.35rem', color: dark ? '#aab1ba' : '#475569', fontSize: '0.95rem' }}>
        {en('volunteer.card.body')}
      </p>
      <p style={{ margin: '0 0 0.9rem', ...amText }}>{am('volunteer.card.body')}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {tierBtn('call', '📞', 'volunteer.cta.call')}
        {tierBtn('video', '🎬', 'volunteer.cta.video')}
      </div>
      {dismissible && (
        <button
          onClick={() => {
            track('story.dismissed', { surface: source });
            onDismiss?.();
          }}
          style={{
            display: 'block',
            margin: '0.7rem auto 0',
            background: 'transparent',
            border: 'none',
            color: dark ? '#7d8590' : '#94a3b8',
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          {en('volunteer.dismiss')} · {am('volunteer.dismiss')}
        </button>
      )}
    </div>
  );
};

export default StoryInviteCard;
